// Mission system: ONE simple mission at a time — guides new (young) players step by step.
// Also: grandma bailout — you can never go broke.
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { SPECIES } from './animalCatalog.js';

const MISSIONS = [
  { id: 'path',     icon: '🛤️', text: 'Build a path from the gate',        check: (w, b) => w.build.paths.size >= b.paths + 5,          reward: 2000 },
  { id: 'fence',    icon: '🚧', text: 'Make a new habitat (drag a box!)',  check: (w, b) => w.build.enclosures.length >= b.enclosures + 1, reward: 3000 },
  { id: 'animal',   icon: '🦁', text: 'Put 2 animals in your habitat',     check: (w, b) => w.animals.animals.length >= b.animals + 2,  reward: 4000 },
  { id: 'stand',    icon: '🍦', text: 'Build a food or drink stand',       check: (w) => w.amenities.items.length >= 1,                 reward: 3000 },
  { id: 'visitors', icon: '🎉', text: 'Welcome 20 visitors',               check: () => store.get().visitorCount >= 20,                 reward: 5000 },
  { id: 'baby',     icon: '🍼', text: 'Breed a baby animal (happy pairs!)',check: (w) => w.animals.animals.some((a) => a.maturity < 0.5), reward: 6000 },
  { id: 'grow',     icon: '⭐', text: 'Reach 60% reputation',              check: () => store.get().reputation >= 60,                   reward: 8000 },
  { id: 'big',      icon: '🏆', text: 'Have 3 habitats with animals',      check: (w, b) => new Set(w.animals.animals.map((a) => a.enclosureId)).size >= b.encWithAnimals + 2, reward: 10000 },
];

export const TROPHIES = [
  { id: 'first_sticker', icon: '🏅', label: 'First Friend',     desc: 'Collect your first animal' },
  { id: 'first_baby',    icon: '🍼', label: 'First Baby',       desc: 'Breed a baby animal' },
  { id: 'collector5',    icon: '📖', label: 'Collector',        desc: '5 species collected' },
  { id: 'collector15',   icon: '📚', label: 'Super Collector',  desc: '15 species collected' },
  { id: 'collector30',   icon: '👑', label: 'Master Zoologist', desc: 'All 30 species!' },
  { id: 'crowd50',       icon: '🎪', label: 'Crowd Favorite',   desc: '50 visitors at once' },
  { id: 'threestar',     icon: '⭐', label: 'Rising Star',      desc: 'Reach 3 stars' },
  { id: 'fivestar',      icon: '🌟', label: 'Grand Zoo',        desc: 'Reach 5 stars' },
  { id: 'rich',          icon: '💰', label: 'Money Maker',      desc: 'Own $100,000' },
];

const TROPHY_CHECKS = {
  first_sticker: (w, s) => (s.collected || []).length >= 1,
  first_baby:    (w) => w.animals.animals.some((a) => a.maturity < 1),
  collector5:    (w, s) => (s.collected || []).length >= 5,
  collector15:   (w, s) => (s.collected || []).length >= 15,
  collector30:   (w, s) => (s.collected || []).length >= 30,
  crowd50:       (w, s) => s.visitorCount >= 50,
  threestar:     (w, s) => (s.stars || 1) >= 3,
  fivestar:      (w, s) => (s.stars || 1) >= 5,
  rich:          (w, s) => s.money >= 100000,
};

export class MissionSystem {
  constructor(world) {
    this.world = world;
    this.index = 0;
    const saved = store.get().missionProgress;
    if (saved) { this.index = saved.index || 0; this._base = saved.base || null; }
    this._grandmaMonth = null;
    this._publish();
    this._subs = [
      bus.on('sim:newDay', () => this.dailyTick()),
    ];
    // Check mission progress on every relevant event + a slow poll for stat-based ones
    this._poll = setInterval(() => this.checkProgress(), 1500);
  }
  dispose() { this._subs.forEach((u) => u()); clearInterval(this._poll); }

  _publish() {
    const m = MISSIONS[this.index] || null;
    store.set({ mission: m ? { id: m.id, icon: m.icon, text: m.text, reward: m.reward } : null });
  }

  checkProgress() {
    if (store.get().screen !== 'game') return;
    // --- Trophies: permanent milestone badges, +5 prestige each
    {
      const s = store.get();
      const owned = new Set(s.trophies || []);
      let gained = false;
      TROPHIES.forEach((t) => {
        if (owned.has(t.id)) return;
        let ok = false;
        try { ok = TROPHY_CHECKS[t.id](this.world, s); } catch { /* booting */ }
        if (ok) {
          owned.add(t.id);
          gained = true;
          pushNews(`Trophy earned: ${t.icon} ${t.label} — ${t.desc}!`, 'success');
          bus.emit('ui:toast', { text: `🏆 Trophy: ${t.label}!`, kind: 'success' });
        }
      });
      if (gained) {
        store.set({ trophies: [...owned], prestige: store.get().prestige + 5 });
        bus.emit('ui:celebrate', { kind: 'mission' });
      }
    }
    // --- Collection stickerbook: first time you own a species = sticker + reward
    {
      const s = store.get();
      const have = new Set(s.collected || []);
      const fresh = [];
      this.world.animals.animals.forEach((a) => { if (!have.has(a.species)) { have.add(a.species); fresh.push(a.species); } });
      if (fresh.length) {
        store.set({ collected: [...have], prestige: s.prestige + fresh.length * 3 });
        fresh.forEach((key) => {
          const sp = SPECIES[key];
          pushNews(`New sticker: the ${sp.label} joined your collection! (+3 prestige)`, 'success');
          bus.emit('ui:toast', { text: `📖 Sticker collected: ${sp.label}!`, kind: 'success' });
        });
        bus.emit('ui:celebrate', { kind: 'mission' });
      }
    }
    // --- Zoo star rating (1-5 ⭐): the kid-readable version of reputation
    {
      const s = store.get();
      const species = new Set(this.world.animals.animals.map((a) => a.species)).size;
      const score = s.reputation * 0.5 + s.prestige * 0.3 + species * 6 + Math.min(40, s.visitorCount);
      const stars = score >= 190 ? 5 : score >= 130 ? 4 : score >= 80 ? 3 : score >= 40 ? 2 : 1;
      if (stars !== (s.stars || 1)) {
        store.set({ stars });
        if (stars > (s.stars || 1)) {
          pushNews(`Your zoo earned ${stars} stars!`, 'success');
          bus.emit('ui:toast', { text: `⭐ ${stars}-star zoo!`, kind: 'success' });
          bus.emit('ui:celebrate', { kind: 'mission' });
        }
      }
    }
    // Announce newly unlocked species when prestige crosses a threshold
    const prestige = store.get().prestige;
    if (this._lastPrestige == null) this._lastPrestige = prestige;
    if (prestige > this._lastPrestige) {
      Object.entries(SPECIES).forEach(([key, sp]) => {
        if (sp.minPrestige > this._lastPrestige && sp.minPrestige <= prestige) {
          pushNews(`NEW ANIMAL UNLOCKED: the ${sp.label}! Check the animal shop.`, 'success');
          bus.emit('ui:toast', { text: `🔓 New animal unlocked: ${sp.label}!`, kind: 'success' });
          bus.emit('ui:celebrate', { kind: 'mission' });
        }
      });
      this._lastPrestige = prestige;
    }
    // Baseline captured once, so a pre-built starter habitat doesn't auto-complete missions.
    if (!this._base) {
      const w = this.world;
      this._base = {
        paths: w.build.paths.size,
        enclosures: w.build.enclosures.length,
        animals: w.animals.animals.length,
        encWithAnimals: new Set(w.animals.animals.map((a) => a.enclosureId)).size,
      };
      store.set({ missionProgress: { index: this.index, base: this._base } });
    }
    const m = MISSIONS[this.index];
    if (!m) return;
    let done = false;
    try { done = m.check(this.world, this._base); } catch { /* world still booting */ }
    if (done) {
      store.set({ money: store.get().money + m.reward });
      pushNews(`Mission complete: ${m.text} — reward $${m.reward.toLocaleString('en-US')}!`, 'success');
      bus.emit('ui:toast', { text: `${m.icon} Mission complete! +$${m.reward.toLocaleString('en-US')}`, kind: 'success' });
      bus.emit('ui:celebrate', { kind: 'mission' });
      this.index++;
      store.set({ missionProgress: { index: this.index, base: this._base } });
      this._publish();
    }
  }

  dailyTick() {
    // Daily gift (kids mode): a little present every 5th day keeps the loop sweet
    const s0 = store.get();
    if (s0.kidsMode) {
      this._giftDay = (this._giftDay || 0) + 1;
      if (this._giftDay % 5 === 0) {
        const gift = 500 + Math.floor(Math.random() * 4) * 250;
        store.set({ money: s0.money + gift });
        bus.emit('ui:toast', { text: `🎁 Daily surprise: +$${gift}!`, kind: 'success' });
      }
    }
    // Grandma bailout: never stuck at zero — free $5000, max once per month
    const s = store.get();
    const monthKey = s.year * 12 + s.month;
    const threshold = s.kidsMode ? 2000 : 500; // grandma helps sooner in kids mode
    if (s.money < threshold && this._grandmaMonth !== monthKey) {
      this._grandmaMonth = monthKey;
      store.set({ money: s.money + 5000 });
      pushNews('Grandma slipped you $5,000 — "Go build something nice, dear." 👵', 'success');
    }
  }
}
